import { TooltipButton } from '@/components/ui/TooltipButton'
import { Button } from '@/components/shadcn/button'
import { DropdownMenuItem } from '@/components/shadcn/dropdown-menu'
import { DataTableV2NavigationAction } from '../types'
import { CellContext } from '@tanstack/react-table'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { Link, useSearchParams } from 'react-router'
import { CATALOG_BASE_PATH } from '@/const'
import { useSubmitPageFilters } from '@/catalog-apps/components/AppPage2/Filter/useSubmitPageFilters'
import { FormValues } from '../../FormComponent/types'
import { useCatalogComponentRuntime } from '../../shared/CatalogComponentRuntimeContext'
import type { MouseEvent } from 'react'

type NavigationItemProps<T, V> = {
  action: DataTableV2NavigationAction
  info: CellContext<T, V>
  displayAs?: 'dropdown-item' | 'icon-button'
}

export function NavigationMenuItem<T, V>({ action, info, displayAs = 'dropdown-item' }: NavigationItemProps<T, V>) {
  const { submitFilters } = useSubmitPageFilters()
  const [searchParams] = useSearchParams()
  const { allowRouteNavigation = true } = useCatalogComponentRuntime()
  const { pageId: pageIdParam, pageFilters: pageFiltersParam } = action.parameterMappings

  const pageId = (info.row.original as Record<string, string>)[pageIdParam]
  const pageFilters: FormValues | undefined = pageFiltersParam
    ? (info.row.original as Record<string, FormValues>)[pageFiltersParam]
    : undefined

  const handleNavigate = (event: MouseEvent<HTMLAnchorElement>) => {
    if (action.isDisabled) {
      event.preventDefault()
      return
    }
    if (pageFilters) {
      submitFilters(pageFilters)
    }
  }

  const icon = (
    <DynamicIcon
      iconName={action.icon || ''}
      lazyLoad={false}
      className={displayAs === 'icon-button' ? undefined : 'size-3'}
    />
  )

  if (!allowRouteNavigation) {
    if (displayAs === 'icon-button') {
      return (
        <TooltipButton tooltip={action.label}>
          <Button variant="ghost" size="icon" className="size-6" disabled>
            {icon}
            <span className="sr-only">{action.label}</span>
          </Button>
        </TooltipButton>
      )
    }

    return (
      <DropdownMenuItem disabled>
        <div className="flex items-center gap-2">
          {icon}
          <span>{action.label}</span>
        </div>
      </DropdownMenuItem>
    )
  }

  if (displayAs === 'icon-button') {
    return (
      <TooltipButton tooltip={action.label}>
        <Link
          className="text-foreground"
          to={`${CATALOG_BASE_PATH}${pageId}?${searchParams.toString()}`}
          onClick={handleNavigate}
        >
          <Button variant="ghost" size="icon" className="size-6" disabled={action.isDisabled}>
            {icon}
            <span className="sr-only">{action.label}</span>
          </Button>
        </Link>
      </TooltipButton>
    )
  }

  return (
    <Link
      className="text-foreground"
      to={`${CATALOG_BASE_PATH}${pageId}?${searchParams.toString()}`}
      onClick={handleNavigate}
    >
      <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={action.isDisabled}>
        <div className="flex items-center gap-2">
          {icon}
          <span>{action.label}</span>
        </div>
      </DropdownMenuItem>
    </Link>
  )
}
